import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useThemeMode, ThemePreference } from '../../context/ThemeContext'
import { useAuth } from '../../context/AuthContext'
import { useSnackbar } from '../../context/snackbar-context'
import settingsApi, {
  SettingsResponse,
  NotificationsSettings,
} from '../../client/settings-api'

// One section is visible at a time; this is the nav's source of truth.
export type SectionKey =
  | 'profile'
  | 'preferences'
  | 'notifications'
  | 'privacy'
  | 'data'

const SETTINGS_CACHE_KEY = 'settings-cache'

const readCache = (): SettingsResponse | null => {
  try {
    const cached = localStorage.getItem(SETTINGS_CACHE_KEY)
    return cached ? (JSON.parse(cached) as SettingsResponse) : null
  } catch {
    return null
  }
}

const writeCache = (data: SettingsResponse) => {
  localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(data))
}

export const useSettings = () => {
  const { getAccessToken, isAuthenticated } = useAuth()
  const { mode, preference, setPreference } = useThemeMode()
  const { enqueueSnackbar } = useSnackbar()
  const [searchParams, setSearchParams] = useSearchParams()

  const [settings, setSettings] = useState<SettingsResponse | null>(
    () => readCache(), // load cache immediately on mount - no auth guard
  )
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [refreshing, setRefreshing] = useState(false)

  // Deep links from notification emails (?highlight=notifications) open that
  // section directly. The section IS the destination now, so there's nothing to
  // scroll to and nothing to dim - the only lasting effect is a one-shot "came
  // from your email" marker that clears as soon as the user navigates by hand.
  const cameFromEmail = searchParams.get('highlight') === 'notifications'
  const [activeSection, setActiveSection] = useState<SectionKey>(() =>
    cameFromEmail ? 'notifications' : 'profile',
  )
  const [showEmailOrigin, setShowEmailOrigin] = useState(cameFromEmail)

  // Strip the param on arrival so a refresh doesn't resurrect the marker after
  // the user has dismissed it by navigating; the section stays selected.
  useEffect(() => {
    if (!cameFromEmail) return
    const next = new URLSearchParams(searchParams)
    next.delete('highlight')
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameFromEmail])

  const selectSection = (section: SectionKey) => {
    setActiveSection(section)
    setShowEmailOrigin(false)
  }

  const isThemePreference = (value: string): value is ThemePreference =>
    value === 'light' || value === 'dark' || value === 'system'

  // Sync theme from cache on first render
  useEffect(() => {
    const cached = readCache()
    if (cached && isThemePreference(cached.theme) && cached.theme !== preference)
      setPreference(cached.theme)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fetch fresh from API once authenticated
  useEffect(() => {
    if (!isAuthenticated) return

    const fetchSettings = async () => {
      const hasCached = !!readCache()
      if (hasCached) {
        setRefreshing(true)
      } else {
        setLoading(true)
      }

      try {
        const token = await getAccessToken()
        const res = await settingsApi.get(token)
        setSettings(res.data)
        writeCache(res.data)
        if (isThemePreference(res.data.theme) && res.data.theme !== preference)
          setPreference(res.data.theme)
      } catch {
        /* swallow - cached data stays visible */
      } finally {
        setLoading(false)
        setRefreshing(false)
      }
    }

    fetchSettings()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  const updateRemote = async (
    payload: Partial<SettingsResponse> & { notificationsAll?: boolean },
  ) => {
    if (!isAuthenticated) return
    setSaving(true)
    try {
      const token = await getAccessToken()
      const res = await settingsApi.update(token, payload)
      setSettings(res.data)
      writeCache(res.data)
    } catch {
      /* ignore */
    } finally {
      setSaving(false)
      enqueueSnackbar('Setting saved', { variant: 'success' })
    }
  }

  // Handles booleans, the frequency string, and the quietHours object alike.
  const handleNotificationChange = <K extends keyof NotificationsSettings>(
    key: K,
    value: NotificationsSettings[K],
  ) => {
    setSettings((prev) => {
      if (!prev) return prev
      const next = { ...prev.notifications, [key]: value }
      updateRemote({ notifications: next })
      return { ...prev, notifications: next }
    })
  }

  const handleAllNotificationsToggle = (value: boolean) => {
    setSettings((prev) => {
      if (!prev) return prev
      const next: NotificationsSettings = {
        ...prev.notifications,
        emailNotifications: value,
        newEpisodeAlerts: value,
        newSeasonAlerts: value,
        recommendations: value,
        newReleases: value,
        commentReplies: value,
      }
      updateRemote({
        notifications: next,
        notificationsAll: value,
        activityReminders: value,
        recapAndMilestones: value,
      })
      return {
        ...prev,
        notifications: next,
        activityReminders: value,
        recapAndMilestones: value,
      }
    })
  }

  const handleTopLevelToggle = (
    key: 'activityReminders' | 'recapAndMilestones',
    value: boolean,
  ) => {
    setSettings((prev) => {
      if (!prev) return prev
      const next = { ...prev, [key]: value }
      updateRemote({ [key]: value })
      return next
    })
  }

  const handleThemeChange = (nextTheme: ThemePreference) => {
    setPreference(nextTheme)
    if (settings) {
      setSettings({ ...settings, theme: nextTheme })
      updateRemote({ theme: nextTheme })
    }
  }

  return {
    settings,
    loading,
    saving,
    refreshing,
    isDisabled: saving || loading || refreshing,
    mode,
    themePreference: preference,
    activeSection,
    selectSection,
    showEmailOrigin,
    handleNotificationChange,
    handleAllNotificationsToggle,
    handleTopLevelToggle,
    handleThemeChange,
    updateRemote,
    setSettings,
  }
}
